import { AimOutlined, EnvironmentFilled, EnvironmentOutlined } from '@ant-design/icons';
import { AutoComplete, Input, Skeleton } from 'antd';
import { reverseGeoCode } from 'api/geocode';
import useAutoComplete from 'hooks/useAutoComplete';
import React, { useEffect, useState } from 'react';
import {
  GoogleMap, useJsApiLoader, Marker,
} from '@react-google-maps/api';
import { captureException } from 'utils/errors';
import { getGeoCode } from 'utils/places';

function normalizeLatLng(latLng) {
  if (!latLng) return {};
  return {
    lat: typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat,
    lng: typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng,
  };
}

function BaseMap({
  children,
  onChange = () => {
  },
  center: defaultCenter,
  height = 300,
  style,
  ...props
}) {
  const [map, setMap] = useState(null);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyBiGU97-Nd44zat6xJvd93Na6l2m61Y-Zw',
    libraries: ['places', 'drawing'],
  });

  const debouncedReverseGeocode = React.useCallback(
    React.useMemo(() => {
      let timeoutId;
      return (newCenter) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          reverseGeoCode(normalizeLatLng(newCenter))
            .then((text) => {
              onChange({ ...normalizeLatLng(newCenter), text });
            })
            .catch((e) => {
              captureException(e);
              onChange(normalizeLatLng(newCenter));
            });
        }, 1000);
      };
    }, [onChange]),
    [onChange],
  );

  const onCenterChange = (newCenter) => {
    debouncedReverseGeocode(newCenter);
  };

  const onLoad = React.useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = React.useCallback((map) => {
    setMap(null);
  }, []);

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={{
        height,
        width: '100%',
        ...style,
      }}
      center={defaultCenter}
      zoom={15}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={(e) => onCenterChange(e.latLng)}
      onCenterChanged={() => {
        if (!map) {
          return;
        }
        onCenterChange(map.getCenter());
      }}
      options={{
        streetViewControl: false,
        mapTypeControl: false,
        gestureHandling: 'greedy',
        styles: [
          {
            featureType: 'poi',
            stylers: [
              { visibility: 'off' },
            ],
          },
        ],
        ...props,
      }}
    >
      {children}
    </GoogleMap>
  ) : <MapSkeleton height={height} />;
}

const Map = React.memo(BaseMap);

Map.FixedMarker = function (props) {
  return (
    <EnvironmentFilled
      {...props}
      style={{
        position: 'absolute',
        left: 'calc( 50% - 15px)',
        top: 'calc( 50% - 15px)',
        fontSize: 32,
        cursor: 'grab',
        ...props.style,
      }}
    />
  );
};

Map.Marker = Marker;

Map.SearchInput = function SearchInput({
  onChange, value: defaultAddress, hasCurrentLocationSelector = true, ...props
}) {
  const [value, setValue] = useState('');
  const options = useAutoComplete(value);
  async function handleSelect(_, selectedOption) {
    if (!selectedOption || typeof selectedOption !== 'object') {
      return;
    }
    const {
      place_id: placeId,
      description: text,
    } = selectedOption;
    const { lat, lng } = await getGeoCode(placeId);
    onChange({ lat, lng, text });
  }

  useEffect(() => {
    if (defaultAddress?.place_id) handleSelect('', defaultAddress);
  }, [defaultAddress]);

  const handleCurrentLocation = async () => {
    if (navigator?.geolocation) {
      await navigator.geolocation.getCurrentPosition(success, fail);
    } else {
      alert('You should set location permission');
    }

    function success(position) {
      const { coords: { latitude: lat, longitude: lng } } = position;
      onChange({ lat, lng });
    }

    function fail() {
      alert('You should set location permission!');
    }
  };

  return (
    <AutoComplete
      options={options}
      style={{ width: '85%', top: 10, left: 8 }}
      onSelect={handleSelect}
      onSearch={setValue}
    >
      <Input
        prefix={<EnvironmentOutlined />}
        addonAfter={hasCurrentLocationSelector ? <AimOutlined onClick={handleCurrentLocation} /> : null}
        placeholder="Enter the delivery address"
        size="large"
      />
    </AutoComplete>
  );
};

function MapSkeleton({ height }) {
  return (
    <Skeleton.Input block fullSize active style={{ height }} />
  );
}

export default Map;
